import lodash from 'lodash';
import React from 'react';

import { useDevAiRequestService } from './../../ricos-context';
import { useRequestService } from './../../_shared/request-service-provider/request-service-provider';

import type { ImageStyle } from '../ai-image-modal/slice/types';
import type { AiPluginEditorConfig } from '../types';

const API_ENDPOINT = 'https://www.wix-pages.com/_serverless/ricos-ai-gateway';

export type RequestPrompt =
  | { prompt: string; additionalTags?: string }
  | { promptId: string; promptParams?: Record<string, string>; additionalTags?: string }
  | { promptSlug: string; promptParams?: Record<string, string>; additionalTags?: string };

type FetchPromptResponse<R = string[]> =
  | { type: 'SUCCESS'; results: R }
  | { type: 'ERROR'; error: string };

export type AiRequestService = {
  fetchPrompt: (prompt: RequestPrompt, content?: string) => Promise<FetchPromptResponse>;
  fetchImagePrompt: (body: {
    prompt: string;
    imageStyle: ImageStyle;
  }) => Promise<FetchPromptResponse<{ url: string; blob: Blob }[]>>;
  fetchPromptFromSelection: (body: { selection: string }) => Promise<FetchPromptResponse>;
};

const pluckErrorMessage = (error: unknown, fallback: string): string =>
  lodash.isObject(error) && 'message' in error && lodash.isString(error.message)
    ? (error.message as string)
    : fallback;

export const useAiRequestService = (config: AiPluginEditorConfig): AiRequestService => {
  const { makeRequest } = useRequestService();
  const devAiRequestService = useDevAiRequestService();

  if (devAiRequestService) {
    return devAiRequestService;
  }

  const gatewayBaseUrl = config.getGatewayBaseUrl ? config.getGatewayBaseUrl() : API_ENDPOINT;
  const appDefId = config.appDefId;

  const fetchPrompt = async (prompt: RequestPrompt, content?: string) => {
    try {
      const response = await makeRequest(`${gatewayBaseUrl}/prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          ...prompt,
          content,
          appDefId,
        }),
      });

      const results: string[] = await response.json();

      return { type: 'SUCCESS', results } as const;
    } catch (error) {
      return { type: 'ERROR', error: pluckErrorMessage(error, '') } as const;
    }
  };

  const fetchImagePrompt = async (body: { prompt: string; imageStyle: ImageStyle }) => {
    try {
      const response = await makeRequest(`${gatewayBaseUrl}/image-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          userInput: body.prompt,
          imageStyle: body.imageStyle === 'NONE' ? undefined : body.imageStyle,
          appDefId,
        }),
      });

      const generatedResults: { url: string }[] = await response.json();

      const results = await Promise.all(
        generatedResults.map(async ({ url }) => {
          const blob = await fetch(url).then(response => response.blob());
          return { url: window.URL.createObjectURL(blob), blob };
        })
      );

      return { type: 'SUCCESS', results } as const;
    } catch (error) {
      return { type: 'ERROR', error: pluckErrorMessage(error, '') } as const;
    }
  };

  const fetchPromptFromSelection = async (body: { selection: string }) => {
    try {
      const response = await makeRequest(`${gatewayBaseUrl}/convert-text-to-prompt`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          selectedText: body.selection,
          appDefId,
        }),
      });

      const results: string[] = await response.json();

      return { type: 'SUCCESS', results } as const;
    } catch (error) {
      return { type: 'ERROR', error: pluckErrorMessage(error, '') } as const;
    }
  };

  return React.useMemo(() => {
    return {
      fetchPrompt,
      fetchImagePrompt,
      fetchPromptFromSelection,
    };
  }, [makeRequest]);
};
